import { isExactMyAisChannel } from "@app/shared/constants/channel.constants";

/**
 * Source of Fund (`isSof`) lives on each PRICE, as
 * `prodSpecCharValueUse[name="isSof"]`, and only myAIS authors it:
 *
 * | offering | price type | control  | default |
 * |----------|------------|----------|---------|
 * | prepaid  | any        | editable | true    |
 * | postpaid | any        | editable | false   |
 *
 * Price type no longer decides anything. Until 2026-09-24 a postpaid price
 * that was not one-time was locked, which left an author looking at a flag
 * they could see, could not set, and had no way to ask for. Every postpaid
 * price is now authored the same way and simply starts from false.
 *
 * The catalog version (PLM 1.0.4 / CPC 2.0.0) plays no part. The backend
 * enforces the same table (`internal/enrichment/is_sof.go`), so this shapes
 * the screen and the backend guards the data. Both sides moved together: a
 * portal that offered the control while the backend still dropped the write
 * would lose the author's edit without a word.
 */
export interface IsSofRule {
  /** False outside an explicit myAIS edit scope: render nothing. */
  visible: boolean;
  /** True wherever the control is visible. Kept as a field because the
   *  channel arm below still answers false, and the template reads one flag
   *  rather than re-deriving the scope rule. */
  editable: boolean;
  /** What to show for a price that has never had a value. Display only —
   *  it is not written until the author changes the toggle. */
  defaultValue: boolean;
}

export interface IsSofRuleInput {
  channel: string | null | undefined;
  productType: string | null | undefined;
  /** Read no longer: every price type is authored the same way. Kept on the
   *  input so callers need not change, and so a future rule that does depend
   *  on it has the value to hand. */
  priceType?: string | null | undefined;
}

/** Lower-cased letters only, so "Pre-paid" / "PRE_PAID" / "prepaid" compare
 *  equal — same normalisation the Prices tab already uses. */
function lettersOnly(value: string | null | undefined): string {
  return (value ?? "").toLowerCase().replaceAll(/[^a-z]/g, "");
}

export function isSofRule(input: IsSofRuleInput): IsSofRule {
  // Exact, not the lenient isMyAisChannel: an unpicked scope must not offer a
  // control whose save the backend would drop.
  if (!isExactMyAisChannel(input.channel)) {
    return { visible: false, editable: false, defaultValue: false };
  }
  // Only the DEFAULT differs now. An offering with no product type takes the
  // postpaid arm, as it always has: nothing proves such a document is
  // prepaid, and starting the flag off is the safer of the two.
  return {
    visible: true,
    editable: true,
    defaultValue: lettersOnly(input.productType) === "prepaid",
  };
}

/** The value the toggle shows: the price's own when it has one, the rule's
 *  default otherwise. */
export function resolveIsSof(
  stored: boolean | null | undefined,
  rule: IsSofRule,
): boolean {
  return typeof stored === "boolean" ? stored : rule.defaultValue;
}
